import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Crosshair, Radar, Rocket, Anchor, Eye, Zap } from "lucide-react";
import hero from "@/assets/public/about-hero.jpg";
import grantPhoto from "@/assets/grant-rogers-headshot.jpg.asset.json";
import dennisPhoto from "@/assets/dennis-zanoni-headshot.jpg.asset.json";
import reneePhotoSrc from "@/assets/renee-watkins-headshot-bright.jpg";
const reneePhoto = { url: reneePhotoSrc };
import pkPhotoSrc from "@/assets/pk-kelley-headshot-bright.jpg";
const pkPhoto = { url: pkPhotoSrc };
import tomPhotoSrc from "@/assets/tom-barnett-headshot-polished.jpg";
const tomPhoto = { url: tomPhotoSrc };
import brianPhoto from "@/assets/brian-westgate-headshot.jpg";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Eyebrow, Shell, capabilities } from "@/components/public/site";

export const Route = createFileRoute("/about/")({
  head: () => ({
    meta: [
      { title: "About — Maven Scouts" },
      { name: "description", content: "Maven Scouts' story, Gulf South roots, core capabilities, and leadership team." },
      { property: "og:title", content: "About — Maven Scouts" },
      { property: "og:description", content: "Our story, capabilities, and leadership — rooted in the Gulf South's defense heritage." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Page,
});

const capIcons = [Crosshair, Radar, Rocket];

const values = [
  { icon: Anchor, title: "Bold", body: "We take on hard problems and move decisively to put capability in the hands of those who need it." },
  { icon: Eye, title: "Vigilant", body: "We watch the market, the threat, and the mission closely — so our partners are never caught off guard." },
  { icon: Zap, title: "Innovative", body: "We bridge commercial ingenuity and defense requirements with new ways to network, build, and deploy." },
];

const timeline = [
  ["MavenWERX", "Accelerator", "Our accelerator helps founders understand customers, strengthen business models, and prepare for capital."],
  ["Support", "Technical assistance & workshops", "One-on-one technical assistance, workshops, pitch competitions, and industry events."],
  ["Sectors", "Where we work", "Dual-use technology, energy, advanced manufacturing, national security, and critical infrastructure."],
  ["Network", "Who we connect", "Universities, research institutions, industry leaders, investors, and government partners across the region and beyond."],
  ["Mission", "Why we do it", "Build stronger companies, expand economic opportunity across the Gulf South and Southeast, and strengthen America's industrial base."],
];

const stats = [
  ["25+", "Louisiana small businesses supported since 2018"],
  ["$40M+", "Competitive federal R&D funding"],
  ["$30M+", "Private & corporate investment"],
  ["100+", "Net-new jobs, ~$83K avg. wage"],
];

const team: { name: string; role: string; bio: string; photo?: string; fullBio?: string[] }[] = [
  { name: "Grant Rogers", role: "Founder / CEO", photo: grantPhoto.url, bio: "Leads Maven Scouts' vision and strategy, connecting emerging technology with the operators and partners who need it." },
  {
    name: "Dennis M. “Z” Zanoni",
    role: "Chief of Operations",
    photo: dennisPhoto.url,
    bio: "22-year Air Force veteran bringing deep insight into SBIR programs, nontraditional government acquisition, product fit, and scaling.",
    fullBio: [
      "Dennis M. Zanoni, known as “Z,” is our Chief of Operations and a 22-year Air Force veteran. He brings a broad range of experience and valuable insight into the government sector, including Small Business Innovation Research (SBIR) programs, nontraditional government acquisition pathways, product fit, and scaling.",
      "Z thrives on solving problems, moving initiatives across the finish line, helping people, and making a meaningful difference. His approach is grounded in trust, integrity, collaboration, and a commitment to continuous learning and improvement.",
      "Having lived and traveled around the world, Z appreciates diverse cultures and their cuisines. Outside of work, he enjoys surfing, working out, and attending festivals.",
      "Vision Statement: “Never stop improving. Develop a culture of empowered Airmen driven by innovation, critical thinking, and diversity of thought.”",
      "Motto: “Innovate, Empower, and Inspire.”",
      "Values: “Trust, collaboration, diversity of thought, critical thinking, continuous improvement, implementation, and follow-through—working together to strengthen lethality.”",
    ],
  },
  { name: "Tom Barnett", role: "VP, Business Development", photo: tomPhoto.url, bio: "Builds strategic relationships across government and industry to open new pathways for dual-use technology." },
  {
    name: "Renee Watkins",
    role: "Director of Venture Operations & Performance",
    photo: reneePhoto.url,
    bio: "Leads venture operations, contract performance, and reporting that keep Maven Scouts programs accountable and on track.",
    fullBio: [
      "Renee Watkins, CPD, is a program and operations leader with experience across financial services, defense, government, venture capital, and nonprofit organizations. She serves as Director of Venture Operations & Performance for Maven Scouts and Director of Programs for The Catalyst, where she leads program execution, contract operations, performance reporting, and process improvement.",
      "Previously, as Chief of Staff at Genpact, she supported a $513M Banking & Capital Markets portfolio spanning 67 enterprise accounts across North America, Australia, and Europe.",
      "A 12+ year Army veteran, Renee held high-trust assignments supporting U.S. Army Special Operations Command, the Vice Chief of Staff of the Army, and the Office of the Secretary of Defense. She is a Certified Project Director known for bringing structure, accountability, and clear execution to complex environments.",
    ],
  },
  {
    name: "John “PK” Kelley",
    role: "Director of Business Development",
    photo: pkPhoto.url,
    bio: "U.S. Army Special Operations veteran (2nd Ranger Battalion) advancing sensing, surveillance, and resilient communications for contested environments.",
    fullBio: [
      "PK Kelley is a national security and defense innovation leader serving as Director of Business Development at Maven Scouts. A U.S. Army Special Operations veteran, he served with the 2nd Ranger Battalion, 75th Ranger Regiment, completing three combat deployments to Afghanistan. He brings firsthand operational experience and deep expertise in translating warfighter requirements into deployable, mission-ready technology.",
      "At Maven Scouts, PK focuses on advancing sensing, surveillance, and resilient communications systems designed for contested environments. He connects technology developers, operational users, and industry partners to address mission requirements, close critical capability gaps, and strengthen readiness. His leadership is grounded in an end-user mindset: technology must work where it matters most—in the field, under pressure, and without margin for failure.",
      "Through collaboration with federal and state organizations, including the Texas Military Department and the Department of Homeland Security, PK helps address security challenges involving transnational criminal organizations, terrorism, and foreign state actors. His work bridges operational needs and technology-driven solutions, building relationships that help move innovation toward practical application.",
      "Guided by Maven Scouts’ core values of boldness, vigilance, and innovation, PK approaches business development with a clear purpose: bring the right people, expertise, and technology together to equip those protecting our nation.",
      "Favorite Quote: “We’re not going to talk about what we’re going to accomplish. We’re going to talk about how we’re going to do it.” — Nick Saban",
    ],
  },
  { name: "Brian Westgate", role: "Director, Engineering & Prototyping", photo: brianPhoto, bio: "Oversees rapid prototyping and engineering teams delivering deployable solutions on accelerated timelines." },
];

function Page() {
  return (
    <Shell>
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy text-navy-foreground">
        <img src={hero} alt="New Orleans riverfront with WW2 PT boat, Liberty ship, Higgins boat factory, P-51 and modern drone" width={1920} height={912} className="absolute inset-0 h-full w-full object-cover object-right" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/20" />
        <div className="blueprint absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-6 py-28 md:py-36">
          <Eyebrow tone="dark">About Maven Scouts</Eyebrow>
          <h1 className="mt-8 max-w-3xl font-display text-5xl uppercase leading-[0.95] md:text-7xl">
            Rooted in <span className="text-tint">Louisiana.</span>
          </h1>
          <p className="mt-8 max-w-xl text-xl text-navy-foreground/85">
            Working across the Gulf South and Southeastern United States.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="mx-auto grid max-w-7xl gap-12 px-6 py-24 md:grid-cols-2">
        <div>
          <Eyebrow>Who We Are</Eyebrow>
          <h2 className="mt-6 font-display text-4xl uppercase leading-tight md:text-5xl">Turning ideas into businesses.</h2>
          <p className="mt-6 text-muted-foreground">
            Maven Scouts is a service-disabled veteran-owned commercialization and small business support firm headquartered in Monroe, Louisiana. We help entrepreneurs, startups, and growing companies turn ideas and technologies into businesses by connecting them with the knowledge, relationships, and resources needed to move forward.
          </p>
          <p className="mt-4 text-muted-foreground">
            Through our MavenWERX accelerator, one-on-one technical assistance, workshops, pitch competitions, and industry events, we help founders understand their customers, strengthen their business models, prepare for capital, and pursue new markets.
          </p>
          <p className="mt-4 text-muted-foreground">
            Our work spans dual-use technologies—solutions with both commercial and defense applications—as well as energy, advanced manufacturing, national security, and critical infrastructure. We also support small businesses developing the capabilities they need to compete, grow, and participate in larger industry supply chains.
          </p>
          <p className="mt-4 text-muted-foreground">
            We connect founders with universities, research institutions, industry leaders, investors, and government partners across the region and beyond. From evaluating a technology's commercial potential to preparing for customer introductions and funding opportunities, we focus on practical steps that move companies toward execution.
          </p>
          <p className="mt-6 border-l-4 border-primary pl-4 font-display text-xl uppercase leading-snug">
            Our mission is to build stronger companies, expand economic opportunity across the Gulf South and Southeast, and strengthen America's industrial base.
          </p>
        </div>
        <ol className="relative space-y-8 border-l-2 border-primary/30 pl-8">
          {timeline.map(([when, title, body]) => (
            <li key={when} className="relative">
              <span className="absolute -left-[41px] top-1 h-4 w-4 border-2 border-primary bg-background" />
              <p className="font-mono text-xs uppercase tracking-widest text-primary">{when}</p>
              <h3 className="mt-1 font-display text-xl uppercase">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Values */}
      <section className="border-y border-border bg-surface">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <Eyebrow>Our Mission</Eyebrow>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {values.map(({ icon: Icon, title, body }) => (
              <div key={title} className="border border-border bg-card p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="grid h-12 w-12 place-items-center bg-primary text-primary-foreground"><Icon className="h-6 w-6" /></div>
                <h3 className="mt-6 font-display text-3xl uppercase">{title}</h3>
                <p className="mt-3 text-muted-foreground">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <Eyebrow>Core Capabilities</Eyebrow>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {capabilities.map((c, i) => {
            const Icon = capIcons[i] ?? Rocket;
            return (
              <div key={c.code} className="flex flex-col border border-border bg-card p-8 shadow-sm">
                <div className="flex items-center justify-between">
                  <Icon className="h-8 w-8 text-primary" />
                  <span className="border border-primary/30 bg-accent px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-primary">{c.code}</span>
                </div>
                <h3 className="mt-6 font-display text-2xl uppercase">{c.title}</h3>
                <p className="mt-3 flex-1 text-sm text-muted-foreground">{c.body}</p>
                <ul className="mt-6 space-y-2">
                  {c.points.map((p) => (
                    <li key={p} className="border-l-2 border-primary pl-3 font-mono text-xs uppercase tracking-wider">{p}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Stats */}
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-6 py-14 md:grid-cols-4">
          {stats.map(([n, l]) => (
            <div key={l}>
              <p className="font-display text-5xl">{n}</p>
              <p className="mt-2 font-mono text-xs uppercase tracking-widest opacity-80">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <Eyebrow>Leadership</Eyebrow>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {team.map((m) => (
            <div key={m.name} className="border border-border bg-card p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
              {m.photo ? (
                <img src={m.photo} alt={`${m.name}, ${m.role}`} className="aspect-square w-full border border-border bg-muted object-cover object-top" />
              ) : (
                <div className="grid-bg grid aspect-square place-items-center border border-border font-display text-6xl text-primary">
                  {(() => { const w = m.name.split(" "); return w[0][0] + w[w.length - 1][0]; })()}
                </div>
              )}
              <p className="mt-6 font-mono text-xs uppercase tracking-widest text-primary">{m.role}</p>
              <h3 className="mt-2 font-display text-2xl uppercase">{m.name}</h3>
              <p className="mt-3 text-sm text-muted-foreground">{m.bio}</p>
              {m.fullBio && (
                <Dialog>
                  <DialogTrigger className="mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary hover:underline">
                    Read bio <ArrowRight className="h-4 w-4" />
                  </DialogTrigger>
                  <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
                    <DialogHeader>
                      <p className="font-mono text-xs uppercase tracking-widest text-primary">{m.role}</p>
                      <DialogTitle className="font-display text-3xl uppercase">{m.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
                      {m.fullBio.map((para) => <p key={para.slice(0, 24)}>{para}</p>)}
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          ))}
        </div>
        <div className="mt-16 flex flex-wrap items-center gap-6 border-t border-border pt-10">
          <Link to="/intake" className="bg-primary px-8 py-4 font-mono text-xs uppercase tracking-widest text-primary-foreground shadow-md hover:bg-secondary">Accelerate Now</Link>
          <Link to="/partners" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary">Explore Ecosystem <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </Shell>
  );
}
